/**
 * Live view of a card terminal checkout at the till (#3500).
 *
 * One checkout = optional cash + card payments run one at a time on a
 * terminal (PO decision A3). The server decides everything: a card counts
 * only after SumUp confirms it, and the checkout is recorded once cash and
 * cards cover the amount. This panel polls, shows what is happening on the
 * terminal and offers the next step: another card, stop here, cancel the
 * card on the terminal, or the manual reference fallback (A2).
 */

import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import {
  Alert,
  Box,
  Button,
  Chip,
  CircularProgress,
  FormControl,
  InputLabel,
  Link,
  MenuItem,
  Select,
  Stack,
  TextField,
  Typography,
} from '@mui/material';
import { useNotify } from '@/hooks/useNotify';
import {
  defaultReaderId,
  rememberReaderId,
  useCancelTerminalPayment,
  useCloseTerminalCheckout,
  useManualTerminalCardPayment,
  useResolveTerminalPaymentFailed,
  useStartTerminalCardPayment,
  useTerminalCheckout,
  type TerminalCheckout,
  type TerminalPayment,
  type TerminalPaymentStatus,
} from '@/hooks/useTerminalCheckout';
import { translateBillingError } from '@/lib/billingErrors';
import { centsToEuros, eurosToCents, formatCentsAsEuros, formatCentsForDisplay as formatPrice } from '@/utils/money';
import { isValidCardPaymentReference, normalizeCardPaymentReference } from '@/utils/cardPaymentReference';

const STATUS_CHIPS: Record<TerminalPaymentStatus, { color: 'default' | 'success' | 'error' | 'warning' | 'info'; labelKey: string }> = {
  pending: { color: 'info', labelKey: 'terminal.status.pending' },
  successful: { color: 'success', labelKey: 'terminal.status.successful' },
  failed: { color: 'error', labelKey: 'terminal.status.failed' },
  cancelled: { color: 'default', labelKey: 'terminal.status.cancelled' },
  unknown: { color: 'warning', labelKey: 'terminal.status.unknown' },
};

interface TerminalCheckoutPanelProps {
  checkoutId: string;
  readers: { id: string; name: string }[];
  /** The terminal the till picked for the first card; next cards default to it. */
  initialReaderId?: string;
  /** Called once when the checkout is recorded or abandoned. */
  onFinished: (checkout: TerminalCheckout) => void;
}

function inProgress(checkout: TerminalCheckout): TerminalPayment | undefined {
  return checkout.payments.find((p) => p.status === 'pending' || p.status === 'unknown');
}

export function TerminalCheckoutPanel({ checkoutId, readers, initialReaderId, onFinished }: TerminalCheckoutPanelProps) {
  const { t } = useTranslation('invoices');
  const notify = useNotify();
  const query = useTerminalCheckout(checkoutId);
  const startCard = useStartTerminalCardPayment();
  const manualCard = useManualTerminalCardPayment();
  const close = useCloseTerminalCheckout();
  const cancel = useCancelTerminalPayment();
  const resolveFailed = useResolveTerminalPaymentFailed();

  const [readerId, setReaderId] = useState(() => defaultReaderId(readers, initialReaderId));
  const [amount, setAmount] = useState('');
  const [manual, setManual] = useState(false);
  const [reference, setReference] = useState('');
  const [finished, setFinished] = useState(false);

  const checkout = query.data;

  // The next card defaults to what is left by card, refreshed whenever that
  // changes (a card went through) — not only while the field is empty.
  const remainingCardCents = checkout?.remainingCardCents;
  useEffect(() => {
    if (remainingCardCents === undefined) return;
    setAmount(formatCentsAsEuros(remainingCardCents));
  }, [remainingCardCents]);

  useEffect(() => {
    if (!checkout || finished || checkout.status === 'open') return;
    setFinished(true);
    onFinished(checkout);
  }, [checkout, finished, onFinished]);

  if (!checkout) {
    return query.isError ? (
      <Alert severity="error">{translateBillingError(query.error, t)}</Alert>
    ) : (
      <Box sx={{ display: 'flex', justifyContent: 'center', py: 3 }}>
        <CircularProgress aria-label={t('terminal.loading')} />
      </Box>
    );
  }

  const readerName = (id: string | null) => readers.find((r) => r.id === id)?.name ?? t('terminal.unknownReader');
  const active = inProgress(checkout);
  const successful = checkout.payments.filter((p) => p.status === 'successful');
  const amountCents = eurosToCents(amount) ?? 0;
  const amountValid = amountCents > 0 && amountCents <= checkout.remainingCardCents;
  const referenceNormalized = normalizeCardPaymentReference(reference);
  const referenceValid = isValidCardPaymentReference(referenceNormalized);
  const busy = startCard.isPending || manualCard.isPending || close.isPending || cancel.isPending || resolveFailed.isPending;
  const canAddCard = checkout.status === 'open' && active === undefined && checkout.remainingCardCents > 0;

  const run = async (work: () => Promise<unknown>) => {
    try {
      await work();
    } catch (error) {
      notify.error(t('terminal.actionFailed'), translateBillingError(error, t));
    }
  };

  const sendToTerminal = () =>
    run(async () => {
      rememberReaderId(readerId);
      // The amount stays: a declined card is retried with the same amount.
      await startCard.mutateAsync({ checkoutId, readerId, amountCents });
    });

  const recordManual = () =>
    run(async () => {
      await manualCard.mutateAsync({ checkoutId, amountCents, paymentReference: referenceNormalized });
      setReference('');
      setManual(false);
    });

  return (
    <Stack spacing={2}>
      <Box sx={{ p: 2, bgcolor: 'action.hover', borderRadius: 1 }}>
        {checkout.cashAmountCents > 0 && (
          <Stack direction="row" justifyContent="space-between">
            <Typography variant="body2">{t('terminal.cashPart')}</Typography>
            <Typography variant="body2">{formatPrice(checkout.cashAmountCents)}</Typography>
          </Stack>
        )}
        <Stack direction="row" justifyContent="space-between">
          <Typography variant="body2" fontWeight={600}>
            {t('terminal.remainingByCard')}
          </Typography>
          <Typography variant="body2" fontWeight={600}>
            {formatPrice(checkout.remainingCardCents)}
          </Typography>
        </Stack>
      </Box>

      {checkout.payments.length > 0 && (
        <Stack spacing={1} component="ul" sx={{ listStyle: 'none', p: 0, m: 0 }} aria-label={t('terminal.paymentsList')}>
          {checkout.payments.map((payment) => {
            const chip = STATUS_CHIPS[payment.status];
            return (
              <Stack component="li" key={payment.id} direction="row" spacing={1} alignItems="center" flexWrap="wrap">
                <Typography variant="body2" sx={{ minWidth: 80 }}>
                  {formatPrice(payment.amountCents)}
                </Typography>
                <Chip size="small" color={chip.color} label={t(chip.labelKey)} />
                {payment.cardLast4 !== null && (
                  <Typography variant="caption" color="text.secondary">
                    {t('terminal.cardEnding', { type: payment.cardType ?? '', last4: payment.cardLast4 })}
                  </Typography>
                )}
                {payment.source === 'manual' && (
                  <Typography variant="caption" color="text.secondary">
                    {t('terminal.manualReference', { reference: payment.paymentReference ?? '' })}
                  </Typography>
                )}
              </Stack>
            );
          })}
        </Stack>
      )}

      {active?.status === 'pending' && (
        <Alert
          severity="info"
          icon={<CircularProgress size={18} />}
          action={
            <Button color="inherit" size="small" disabled={busy} onClick={() => void run(() => cancel.mutateAsync(active.id))}>
              {t('terminal.cancelCard')}
            </Button>
          }
        >
          {t('terminal.waitingForCard', { reader: readerName(active.readerId), amount: formatPrice(active.amountCents) })}
        </Alert>
      )}

      {active?.status === 'unknown' && (
        <Alert
          severity="warning"
          action={
            <Button color="inherit" size="small" disabled={busy} onClick={() => void run(() => resolveFailed.mutateAsync(active.id))}>
              {t('terminal.markFailed')}
            </Button>
          }
        >
          {t('terminal.unknownHint')}
        </Alert>
      )}

      {canAddCard && (
        <Stack spacing={1.5}>
          {successful.length > 0 && <Typography variant="subtitle2">{t('terminal.nextCard')}</Typography>}
          <TextField
            type="number"
            size="small"
            label={t('terminal.cardAmount')}
            value={amount}
            onChange={(event) => setAmount(event.target.value)}
            error={amount !== '' && !amountValid}
            helperText={amount !== '' && !amountValid ? t('terminal.cardAmountInvalid') : t('terminal.cardAmountHelp')}
            slotProps={{ htmlInput: { min: 0.01, max: centsToEuros(checkout.remainingCardCents), step: '0.01' } }}
          />
          {manual ? (
            <>
              <TextField
                size="small"
                required
                label={t('payment.cardReference')}
                value={reference}
                onChange={(event) => setReference(event.target.value)}
                error={reference.trim() !== '' && !referenceValid}
                helperText={reference.trim() !== '' && !referenceValid ? t('payment.cardReferenceInvalid') : t('payment.cardReferenceHelp')}
              />
              <Stack direction="row" spacing={1}>
                <Button variant="contained" disabled={!amountValid || !referenceValid || busy} onClick={() => void recordManual()}>
                  {t('terminal.recordManual')}
                </Button>
                <Button onClick={() => setManual(false)}>{t('terminal.backToTerminal')}</Button>
              </Stack>
            </>
          ) : (
            <>
              <FormControl size="small">
                <InputLabel id="terminal-reader-label">{t('terminal.reader')}</InputLabel>
                <Select
                  labelId="terminal-reader-label"
                  label={t('terminal.reader')}
                  value={readerId}
                  onChange={(event) => setReaderId(event.target.value)}
                >
                  {readers.map((reader) => (
                    <MenuItem key={reader.id} value={reader.id}>
                      {reader.name}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
              <Stack direction="row" spacing={1} alignItems="center" flexWrap="wrap">
                <Button variant="contained" disabled={!amountValid || readerId === '' || busy} onClick={() => void sendToTerminal()}>
                  {t('terminal.sendToTerminal')}
                </Button>
                <Link component="button" type="button" variant="body2" onClick={() => setManual(true)}>
                  {t('terminal.enterReferenceManually')}
                </Link>
              </Stack>
            </>
          )}
        </Stack>
      )}

      {checkout.status === 'open' && (
        <Stack direction="row" justifyContent="flex-end">
          <Button
            color={successful.length > 0 ? 'primary' : 'inherit'}
            disabled={active !== undefined || busy}
            onClick={() => void run(() => close.mutateAsync(checkoutId))}
          >
            {successful.length > 0 ? t('terminal.stopHere') : t('terminal.abandon')}
          </Button>
        </Stack>
      )}

      {checkout.status === 'completed' && <Alert severity="success">{t('terminal.completed')}</Alert>}
      {checkout.status === 'partially_recorded' && <Alert severity="info">{t('terminal.partiallyRecorded')}</Alert>}
      {checkout.status === 'abandoned' && <Alert severity="info">{t('terminal.abandoned')}</Alert>}
      {checkout.status === 'record_failed' && <Alert severity="error">{t('terminal.recordFailed')}</Alert>}
    </Stack>
  );
}
